"use client";

import * as Comlink from "comlink";
import { useEffect } from "react";

import {
  Action,
  Channel,
  Status,
  type WorkerToMainMessage,
} from "../types/app/messages";
import { type WorkerAPI } from "../types/app/worker";
import { useProcessEvents } from "./hooks/useProcessEvents";
import { useProcessMessages } from "./hooks/useProcessMessages";
import { useTrackers } from "./hooks/useTrackers";
import { useWorker } from "./hooks/useWorker";

export const WorkerStateUpdater = () => {
  const { setWorker, setStatus } = useWorker();
  const { processMessages } = useProcessMessages();
  const { processEvents } = useProcessEvents();

  useTrackers();

  useEffect(() => {
    const workerInstance = new Worker(
      new URL("#/worker-v2/core/infrastructure/WorkerFacade", import.meta.url),
      {
        type: "module",
      },
    );

    const workerApi = Comlink.wrap<WorkerAPI>(workerInstance);
    setWorker({ api: workerApi });

    const handlers = {
      [Action.Log]: ({ channel, content }: WorkerToMainMessage) => {
        if (channel !== Channel.Status || content !== Status.Ready) return;

        setStatus(Status.Ready);
      },
      [Action.Kill]: () => setStatus(Status.Offline),
      [Action.Message]: processMessages,
      [Action.Event]: processEvents,
    };

    const messageHandler = Comlink.proxy((message: WorkerToMainMessage) =>
      handlers[message.action]?.(message),
    );

    workerApi.setMessageListener(messageHandler);

    return () => {
      workerApi[Comlink.releaseProxy]();
      workerInstance.terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Need to run only once
  }, []);

  return null;
};
